import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  getColor,
  Icons,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import "./index.scss";
import dayjs from "dayjs";
import useLogStore from "../../../redux/dispatcher/useLogStore";
import { LogItem } from "../../../types/files";
import utilsCommon from "../../../shared/utils/common";
import RevisionTag from "../../../components/RevisionTag";
import { SummaryLogItem } from "../../../types/log";
import useCheckboxGroup from "fds/src/hooks/useCheckboxGroup";
import WorkgroupPolicyTooltip from "./WorkgroupPolicyTooltip";
import FilesharePolicyTooltip from "./FilesharePolicyTooltip";
import { handleDocPreview } from "../../../shared/utils/api";
import { t } from "i18next";
import classNames from "classnames";
import EmptyFilterPage from "../../../components/EmptyPage/EmptyFilterPage";
import EmptyInitFilterPage from "../../../components/EmptyPage/EmptyInitFilterPage";
import { FULL_FILTER_LOG_BY_TYPE } from ".";
import i18n from "fds/src/locales/i18n";
import { useNavigate } from "react-router-dom";
import { ChatLogItem } from "../../../types/chat";
import utilsDocs from "../../../shared/utils/docs";

const historyTypeIconName: { [key: string]: string } = {
  webMeeting: "zoom",
  document: "document",
  documentRole: "document",
  workgroup: "people",
  workgroupRole: "people",
  user: "people",
  chat: "chat",
};
export const historyIconByName: {
  [label: string]: { icon: string; name: string };
} = {
  webMeeting_add: { icon: "zoomNew", name: t("log.icon.create") },
  webMeeting_change: { icon: "edit2", name: t("log.icon.changeContent") },
  webMeeting_delete: { icon: "delete", name: t("log.icon.delete") },
  document_add: { icon: "addDoc", name: t("log.icon.share") },
  document_revision: { icon: "edit", name: t("log.icon.edit") },
  document_sign: { icon: "comment", name: t("log.icon.comment") },
  document_change: { icon: "edit2", name: t("log.icon.changeName") },
  document_delete: { icon: "delete", name: t("log.icon.delete") },
  document_download: { icon: "download", name: t("log.icon.download") },
  document_restore: { icon: "redoLeft", name: t("log.icon.restore") },
  document_unwrap: { icon: "unlock", name: t("log.icon.unauthDownload") },
  document_view: { icon: "preview", name: t("log.icon.view") },
  document_succession: {
    icon: "fileOwner",
    name: t("log.icon.changeDocMaster"),
  },
  documentRole_add: {
    icon: "checkedShield",
    name: t("log.icon.changeAuth"),
  },
  documentRole_delete: {
    icon: "checkedShield",
    name: t("log.icon.changeAuth"),
  },
  documentRole_change: {
    icon: "checkedShield",
    name: t("log.icon.changeAuth"),
  },
  document_exView: { icon: "", name: "" },
  document_link: { icon: "link", name: t("log.icon.shareLink") },
  document_webedit_finish: { icon: "edit", name: t("log.icon.edit") },
  workgroup_add: { icon: "workgroupNew", name: t("log.icon.create") },
  workgroup_change: { icon: "edit2", name: t("log.icon.changeInfo") },
  workgroupRole_add: { icon: "shield", name: t("log.icon.changeAuth") },
  workgroupRole_delete: { icon: "shield", name: t("log.icon.changeAuth") },
  workgroupRole_change: { icon: "shield", name: t("log.icon.changeAuth") },
  workgroup_succession: {
    icon: "groupOwner",
    name: t("log.icon.changeWorkgroupMaster"),
  },
  user_change: { icon: "", name: "" },
  user_add: { icon: "user", name: t("log.icon.changeMember") },
  user_invite: { icon: "user", name: t("log.icon.changeMember") },
  user_delete: { icon: "user", name: t("log.icon.changeMember") },
  user_withdraw: { icon: "user", name: t("log.icon.changeMember") },
  workgroup_delete: { icon: "", name: "" },
  workgroup_restore: { icon: "", name: "" },
  workgroup_deleted: { icon: "", name: "" },
  chat_add: { icon: "chatLines", name: t("log.icon.chat") },
  chat_delete: { icon: "chatLines", name: t("log.icon.chat") },
};

const historyIconType = [
  ["webMeeting_add"],
  ["webMeeting_change"],
  ["webMeeting_delete"],
  ["document_add"],
  ["document_revision", "document_webedit_finish"],
  ["document_sign"],
  ["document_change"],
  ["document_delete"],
  ["document_download"],
  ["document_restore"],
  ["document_unwrap"],
  ["document_view"],
  ["document_succession"],
  ["documentRole_add", "documentRole_delete", "documentRole_change"],
  ["document_link"],
  ["workgroup_add"],
  ["workgroup_change"],
  ["workgroupRole_add", "workgroupRole_delete", "workgroupRole_change"],
  ["workgroup_succession"],
  ["user_add", "user_invite", "user_delete", "user_withdraw"],
  ["chat_add", "chat_delete"],
];

type DayGroup = SummaryLogItem[][]; // RunGroup[]
type RunGroup = SummaryLogItem[];

const CompressWorkgroupLog = ({
  workgroupId,
  dateFilter,
  docAsideIndex,
  settingButtonInfo,
  pageType,
  changeDocAsideIndex,
  handleInitFilter,
  setSettingInfo,
  handleGetMeetingDetail,
}: {
  workgroupId: string;
  dateFilter?: {
    month: string;
    year: string;
    startMonth: string;
    startYear: string;
    isCustomDate: boolean;
    customFilter: string[];
  };
  docAsideIndex?: string;
  pageType?: "workgroupHome" | "workgroupLog" | "home";
  changeDocAsideIndex?: (idx: string) => void;
  handleInitFilter?: () => void;
  handleGetMeetingDetail: (meetingId: string, occurrenceId?: string) => void;
  settingButtonInfo?: {
    x: number;
    y: number;
    logId: string;
    log?: LogItem;
  };
  setSettingInfo?: (req: {
    x: number;
    y: number;
    logId: string;
    log?: LogItem;
  }) => void;
}) => {
  const dir = document.documentElement.dir;

  /** Hooks */
  const toast = useToast();
  const checkboxGroup = useCheckboxGroup();
  const { logState, getSummaryLogList, getLogDetail } = useLogStore();
  const navigate = useNavigate();

  /** State */
  const [logs, changeLogs] = useState<DayGroup[]>();

  /** fetch */
  useEffect(() => {
    if (pageType === "workgroupLog" && !dateFilter) return;
    const startDate = dateFilter?.startMonth
      ? dateFilter.isCustomDate
        ? dayjs()
            .set("year", parseInt(dateFilter.startYear ?? undefined))
            .set("month", parseInt(dateFilter.startMonth))
            .startOf("month")
            .valueOf()
        : dayjs()
            .set("year", parseInt(dateFilter.startYear ?? undefined))
            .set("month", parseInt(dateFilter.startMonth))
            .valueOf()
      : dayjs().subtract(1, "month").valueOf();
    const endDate = dateFilter?.month
      ? dateFilter.isCustomDate
        ? dayjs()
            .set("year", parseInt(dateFilter.year ?? undefined))
            .set("month", parseInt(dateFilter?.month ?? undefined))
            .endOf("month")
            .valueOf()
        : dayjs()
            .set("year", parseInt(dateFilter.year ?? undefined))
            .set("month", parseInt(dateFilter?.month ?? undefined))
            // .endOf("month")
            .valueOf()
      : dayjs().valueOf();
    getSummaryLogList({
      workgroupId,
      startDate: startDate + "",
      endDate: endDate + "",
      filterDocument: dateFilter?.customFilter.includes("file")
        ? FULL_FILTER_LOG_BY_TYPE.filterDocument
        : [],
      filterWebMeeting: dateFilter?.customFilter.includes("meeting")
        ? FULL_FILTER_LOG_BY_TYPE.filterWebMeeting
        : [],
      filterWorkgroup: dateFilter?.customFilter.includes("workgroup")
        ? FULL_FILTER_LOG_BY_TYPE.filterWorkgroup
        : [],
      filterChat: dateFilter?.customFilter.includes("chat")
        ? FULL_FILTER_LOG_BY_TYPE.filterChat
        : [],
    });
    checkboxGroup.handleAllClick([]);
  }, [dateFilter, workgroupId]);

  useEffect(() => {
    searchCompressedLog();
    document.getElementById("compressedLog")?.scrollTo(0, 0);
  }, [logState.summaryLogList]);

  /** 스크롤 차단(더보기 열렸을 때) */
  useEffect(() => {
    const target = document.getElementById("compressedLog");
    const target1 = document.getElementById("workgroupHome");
    const target2 = document.getElementById("home");
    if (!target && !target1 && !target2) return;

    const handleScroll = (e: Event) => {
      if (settingButtonInfo?.log) e.preventDefault();
    };
    if (target) {
      target.addEventListener("wheel", handleScroll, { passive: false });
      target.addEventListener("touchmove", handleScroll, { passive: false });
    }
    if (target1) {
      target1.addEventListener("wheel", handleScroll, { passive: false });
      target1.addEventListener("touchmove", handleScroll, { passive: false });
    }
    if (target2) {
      target2.addEventListener("wheel", handleScroll, { passive: false });
      target2.addEventListener("touchmove", handleScroll, { passive: false });
    }
    return () => {
      target?.removeEventListener("wheel", handleScroll);
      target?.removeEventListener("touchmove", handleScroll);
      target1?.removeEventListener("wheel", handleScroll);
      target1?.removeEventListener("touchmove", handleScroll);
      target2?.removeEventListener("wheel", handleScroll);
      target2?.removeEventListener("touchmove", handleScroll);
    };
  }, [settingButtonInfo]);

  /** 사용자 셋 키 */
  const getInvolvedUsersKey = (log: SummaryLogItem) => {
    const ids = (log.involvedUsers ?? [])
      .map((u) => u.userId || u.userName || "")
      .sort();
    return ids.join(",");
  };

  /** 날짜 × 사용자-run 그룹핑 */
  const searchCompressedLog = () => {
    const byDayAndRun: DayGroup[] = [];
    let curDay = "";
    let curRunKey = "";

    logState.summaryLogList?.forEach((item: SummaryLogItem) => {
      const dayKey = utilsCommon.renderDateFormat(item.firstDate, "YYYY-MM-DD");
      const runKey = `${dayKey}||${getInvolvedUsersKey(item)}`;

      if (dayKey !== curDay) {
        byDayAndRun.push([]);
        curDay = dayKey;
        curRunKey = "";
      }
      const dayGroup = byDayAndRun[byDayAndRun.length - 1];

      if (runKey !== curRunKey) {
        dayGroup.push([]);
        curRunKey = runKey;
      }
      const runGroup = dayGroup[dayGroup.length - 1];
      runGroup.push(item);
    });

    changeLogs(byDayAndRun);
  };

  /** 채팅: 줄바꿈 줄이기 */
  const removeEnterTitle = (title?: string) =>
    title && title.includes("\n") ? title.split("\n")[0] + "···" : title;

  /** 채팅 아이템 렌더 */
  const renderChatLogItem = (chat?: ChatLogItem, isClicked?: boolean) =>
    chat?.attachments && chat?.attachments.length > 0 ? (
      <Box
        alignItems="center"
        maxWidth={
          chat.parentMessage ? "calc(100% - 60px)" : "calc(100% - 40px)"
        }
      >
        {utilsDocs.renderDocIcon(chat?.attachments[0].title, 20)}
        <Tooltip
          message={chat?.attachments[0].title}
          classes="mg ml-4"
          maxWidth={"calc(100% - 24px)"}
        >
          <Typography
            type={isClicked ? "subtitle2" : "body2_rg"}
            onClick={() =>
              chat?.messageId &&
              navigate(
                `/workgroup/${workgroupId}/chat?chatId=${chat?.messageId}`
              )
            }
            ellipsis
          >
            {chat?.attachments[0].title ?? t("common.chat")}
          </Typography>
        </Tooltip>
      </Box>
    ) : (
      <Tooltip
        message={chat?.body}
        maxWidth={
          chat?.parentMessage ? "calc(100% - 60px)" : "calc(100% - 40px)"
        }
        hideToolTip={!chat?.body}
      >
        <Typography
          type={isClicked ? "subtitle2" : "body2_rg"}
          onClick={() =>
            chat?.messageId &&
            navigate(`/workgroup/${workgroupId}/chat?chatId=${chat?.messageId}`)
          }
          ellipsis
        >
          {chat?.body ? removeEnterTitle(chat.body) : t("common.chat")}
        </Typography>
      </Tooltip>
    );

  /** 타입별 본문 */
  const renderChatLog = (
    log: SummaryLogItem | LogItem,
    index: string,
    hasChildren: boolean
  ) => {
    const chat =
      "logDetailInfo" in log && log.logDetailInfo?.chatLogData
        ? (JSON.parse(log.logDetailInfo?.chatLogData)[0] as ChatLogItem)
        : "chatLogData" in log && log?.chatLogData
        ? (JSON.parse(log?.chatLogData)[0] as ChatLogItem)
        : undefined;

    return (
      <Box
        // width={
        //   pageType !== "workgroupLog"
        //     ? `calc(100% -  ${300 + ("documnetName" in log ? 190 : 0)}px)`
        //     : `calc(100% - ${350 + ("documnetName" in log ? 190 : 0)}px)`
        // }
        style={{ flex: 1, minWidth: 0 }}
        alignItems="flex-start"
        direction="column"
      >
        {chat?.parentMessage && (
          <Tooltip
            message={
              chat?.parentMessage
                ? chat.parentMessage?.attachments[0]?.title ??
                  chat.parentMessage?.body
                : "-"
            }
            classes="mg mb-8"
          >
            <Typography
              type="body2_rg"
              ellipsis
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {chat?.parentMessage
                ? removeEnterTitle(chat.parentMessage?.attachments[0]?.title) ??
                  removeEnterTitle(chat.parentMessage?.body)
                : "-"}
            </Typography>
          </Tooltip>
        )}
        <Box alignItems="center" width={"100%"}>
          {chat?.parentMessage && (
            <Icons label="downRight" variant="line" classes="mg mr-8" />
          )}
          <Icons
            variant="line"
            size={16}
            classes="mg mr-8"
            label={historyTypeIconName[log.logType]}
          />
          {renderChatLogItem(
            chat,
            checkboxGroup.isSelected(index) && hasChildren
          )}
        </Box>
      </Box>
    );
  };

  const renderWorkgroupLog = (
    log: SummaryLogItem | LogItem,
    index: string,
    hasChildren: boolean
  ) => (
    <Box
      alignItems="center"
      // width={
      //   pageType !== "workgroupLog"
      //     ? `calc(100% -  ${
      //         310 +
      //         ("documnetName" in log ? 190 : 0) +
      //         (i18n.language.includes("en")
      //           ? 50
      //           : i18n.language.includes("ko")
      //           ? 0
      //           : 61)
      //       }px)`
      //     : `calc(100% - ${
      //         360 +
      //         // ("documnetName" in log ? 190 : 0) +
      //         (i18n.language.includes("en")
      //           ? 300
      //           : i18n.language.includes("ko")
      //           ? 0
      //           : 61)
      //       }px)`
      // }
      style={{ flex: 1, minWidth: 0 }}
    >
      <Icons
        variant="line"
        size={16}
        classes="mg mr-8"
        label={historyTypeIconName[log.logType]}
      />
      <Typography
        type={
          checkboxGroup.isSelected(index) && hasChildren
            ? "subtitle2"
            : "body2_rg"
        }
        ellipsis
        maxWidth={"calc(100% - 40px)"}
      >
        {t("common.workgroup")}
      </Typography>
    </Box>
  );

  const renderDocumentLog = (
    log: SummaryLogItem | LogItem,
    index: string,
    hasChildren: boolean
  ) => (
    <Box alignItems="center" style={{ flex: 1, minWidth: 0 }}>
      <Icons
        variant="line"
        size={16}
        classes="mg mr-8"
        label={historyTypeIconName[log.logType]}
      />
      <Tooltip
        maxWidth={"calc(100% - 80px)"}
        message={
          "documentName" in log
            ? log?.documentName
            : "documnetName" in log
            ? log.documnetName
            : ""
        }
      >
        <Typography
          classes={classNames({ "hover-text-underline": log.syncId })}
          type={
            checkboxGroup.isSelected(index) && hasChildren
              ? "subtitle2"
              : "body2_rg"
          }
          ellipsis
          onClick={(e: any) => {
            e.stopPropagation();
            log.syncId &&
              handleDocPreview({
                permission: "view",
                workgroupId,
                syncId: log.syncId,
                toast,
                t,
                revisionNo: log?.revisionNo,
              });
          }}
        >
          {"documentName" in log
            ? log?.documentName
            : "documnetName" in log
            ? log.documnetName
            : ""}
        </Typography>
      </Tooltip>
      <RevisionTag revisionNo={log.revisionNo} classes="mg ml-8 mr-20" />
    </Box>
  );

  /** 본문 타이틀/묶음 문자열 */
  const renderLogDetailStr = (
    log: SummaryLogItem | LogItem | SummaryLogItem[],
    index: string,
    hasChildren: boolean
  ) => {
    if (Array.isArray(log)) {
      let topic = "";
      try {
        topic =
          "desc" in log[0]
            ? log[0].desc
            : log[0].logData
            ? JSON.parse(log[0].logData).topic
            : "";
      } catch (err) {
        topic = log[0].logData ?? "";
      }
      if (
        log[0].logType === "chat" &&
        log.length === 1 &&
        log[0].logIds.length &&
        "logDetailInfo" in log[0]
      )
        return renderChatLog(log[0], index, false);
      const targetList = log.map((item) => {
        if (item.logType.includes("document"))
          return (
            "DOCUMENT_" +
            ("documentName" in item
              ? (item as any)?.documentName
              : "documnetName" in item
              ? (item as any).documnetName
              : "")
          );
        else if (item.logType.includes("chat")) return "CHAT";
        else if (["webMeeting"].includes(item.logType))
          return `MEETING_${topic}`;
        else return "WORKGROUP";
      });
      return (
        <Box alignItems="center" style={{ flex: 1, minWidth: 0 }}>
          <Typography
            // classes=" hover-text-underline"
            type={
              checkboxGroup.isSelected(index) && hasChildren
                ? "subtitle2"
                : "body2_rg"
            }
            ellipsis
          >
            {(log[0].logType.includes("document")
              ? "documentName" in log[0]
                ? (log[0] as any)?.documentName
                : "documnetName" in log[0]
                ? (log[0] as any).documnetName
                : t("common.file")
              : log[0].logType === "chat"
              ? log.length === 1 &&
                log[0].logIds.length &&
                "logDetailInfo" in log[0]
                ? renderChatLog(log[0], index, false)
                : t("common.chat")
              : ["workgroup", "workgroupRole", "user"].includes(log[0].logType)
              ? t("common.workgroup")
              : ["webMeeting"].includes(log[0].logType)
              ? topic
              : log[0].logType) +
              (new Set(targetList).size > 1
                ? ` ${t("common.andNOthers", {
                    num: new Set(targetList).size - 1,
                  })}`
                : "")}
          </Typography>
        </Box>
      );
    }

    // 단일 아이템
    if (log.logType.includes("document")) {
      if ("documentName" in log || "documnetName" in log)
        return renderDocumentLog(log, index, hasChildren);
      return <Box style={{ flex: 1, minWidth: 0 }}>{t("common.file")}</Box>;
    }
    if (log.logType === "chat") return renderChatLog(log, index, hasChildren);
    if (["workgroup", "workgroupRole", "user"].includes(log.logType))
      return renderWorkgroupLog(log, index, hasChildren);

    // 회의 등
    let topic = "";
    try {
      topic =
        "desc" in log
          ? log.desc
          : log.logData
          ? JSON.parse(log.logData).topic
          : "";
    } catch {
      topic = (log as any).logData ?? "";
    }
    return (
      <Box
        alignItems="center"
        // width={
        //   pageType !== "workgroupLog"
        //     ? `calc(100% -  ${300 + ("documnetName" in log ? 190 : 0)}px)`
        //     : `calc(100% - ${350 + ("documnetName" in log ? 190 : 0)}px)`
        // }
        style={{ flex: 1, minWidth: 0 }}
      >
        <Icons
          variant="line"
          size={16}
          classes="mg mr-8"
          label={historyTypeIconName[log.logType]}
        />
        <Tooltip message={topic} maxWidth={"calc(100% - 40px)"}>
          <Typography
            onClick={(e: any) => {
              e.stopPropagation();
              handleGetMeetingDetail(
                log.logData ? JSON.parse(log.logData).id : (log as any).syncId
              );
            }}
            classes="hover-text-underline"
            type={
              checkboxGroup.isSelected(index) && hasChildren
                ? "subtitle2"
                : "body2_rg"
            }
            ellipsis
          >
            {topic}
          </Typography>
        </Tooltip>
      </Box>
    );
  };

  /** 액션 상세 아이콘/툴팁 */
  const renderActionDetail = (log: SummaryLogItem | LogItem, index: string) => {
    const detailLog = "logDetailInfo" in log ? log.logDetailInfo : log;
    return (
      <Tooltip
        message={renderLogDetailLongStr(log)}
        classes="mg ml-auto"
        hideToolTip={
          !(
            (detailLog?.historyType === 1 &&
              [2, 3, 8, 9, 10].includes(detailLog?.saveType)) ||
            (detailLog?.historyType === 2 &&
              [1, 2, 3, 4].includes(detailLog?.saveType))
          )
        }
      >
        {("actionTypeCounts" in log
          ? Object.entries(log.actionTypeCounts).filter(
              ([_, value]) => value !== undefined
            )
          : [[`${log.logType}_${(log as any).actionType}`]]
        ).map(([key]) => (
          <Box alignItems="center" key={`${(log as any).logId || ""}-${key}`}>
            <Icons
              label={historyIconByName[key]?.icon}
              variant="line"
              size="16"
              isOn
            />
            <Typography type="body2_rg" classes="mg ml-8">
              {historyIconByName[key]?.name}
            </Typography>
          </Box>
        ))}
      </Tooltip>
    );
  };

  /** 툴팁 내용(정책 등) */
  const renderLogDetailLongStr = (log: SummaryLogItem | LogItem) => {
    const detailLog = "logDetailInfo" in log ? log.logDetailInfo : log;
    if (detailLog?.historyType === 2) {
      switch (detailLog.saveType) {
        case 1:
          return detailLog.policyLogs &&
            detailLog.policyLogs[0]?.permissionPolicy?.policy
              ?.fileShareLinkPolicy ? (
            <FilesharePolicyTooltip
              linkPolicy={
                detailLog.policyLogs[0].permissionPolicy.policy
                  ?.fileShareLinkPolicy
              }
              logDate={detailLog.logDate}
            />
          ) : (
            detailLog?.desc ??
              (detailLog?.logDatas && !!detailLog?.logDatas[0]
                ? detailLog?.logDatas[0] ?? ""
                : `${t("common.workgroupName")}: ` + detailLog.workgroupName)
          );
        case 2:
          return detailLog.policyLogs && detailLog.policyLogs[0] ? (
            <WorkgroupPolicyTooltip
              logItem={detailLog.policyLogs}
              title={t("workgroup.workgroupDefaultPermssion")}
            />
          ) : (
            t("common.workgroup")
          );
        case 3:
          return detailLog.desc;
        case 4:
          return (
            (["add", "invite"].includes(detailLog.actionType)
              ? t("log.detailLog.inviteUser")
              : t("log.detailLog.excludeUser")) + detailLog.logDatas.join(", ")
          );
        default:
          return t("common.workgroup");
      }
    } else if (detailLog?.historyType === 1) {
      switch (detailLog.saveType) {
        case 2:
          return detailLog.desc;
        case 3:
          return t("log.detailLog.pastFileName") + detailLog.desc;
        case 8:
          return detailLog.desc;
        case 9:
          return detailLog.policyLogs ? (
            <WorkgroupPolicyTooltip logItem={detailLog.policyLogs} />
          ) : (
            t("file.policy.none")
          );
        case 10:
          return detailLog.policyLogs &&
            detailLog.policyLogs[0] &&
            detailLog.policyLogs[0]?.permissionPolicy?.policy
              ?.fileShareLinkPolicy ? (
            <FilesharePolicyTooltip
              linkPolicy={
                detailLog.policyLogs[0].permissionPolicy.policy
                  ?.fileShareLinkPolicy
              }
              logDate={detailLog?.logDate}
            />
          ) : (
            detailLog.documnetName
          );
        default:
          return "";
      }
    }
    return "";
  };

  /** 펼치기(클릭): 해당 run의 대표 summary만 상세 조회 */
  const handleGetLogBelow = (id: string) => {
    const parts = id.split("--"); // YYYY-MM-DD -- runIdx [-- itemIdx]
    const [dateKey, runIdxStr, itemIdxStr] = parts;
    const runIdx = parseInt(runIdxStr ?? "", 10);
    const itemIdx = parseInt(itemIdxStr ?? "0", 10); // ← 핵심

    checkboxGroup.handleClick(id);
    if (checkboxGroup.isSelected(id) || Number.isNaN(runIdx)) return;

    const dayIdx =
      logs?.findIndex(
        (day) =>
          utilsCommon.renderDateFormat(day[0][0].firstDate, "YYYY-MM-DD") ===
          dateKey
      ) ?? -1;
    if (dayIdx < 0) return;

    const run = logs![dayIdx][runIdx];
    if (!run || run.length === 0) return;

    // 클릭한 run 내부 item을 정확히 조회
    const targetSummary = run[itemIdx] ?? run[0];

    getLogDetail({ logIdList: targetSummary.logIds }).then((resp: any) => {
      const detailLogs: LogItem[] =
        (resp.payload?.logResults as LogItem[]) ?? [];
      detailLogs.reverse();
      const childrenLogs = renderCompressedDetailLogs(detailLogs);

      changeLogs((prev) => {
        if (!prev) return prev;
        const cloned = prev.map((day) => day.map((r) => [...r]));
        // 클릭한 item에 childrenLogs 저장 (run[0] 고정 X)
        cloned[dayIdx][runIdx][itemIdx] = {
          ...cloned[dayIdx][runIdx][itemIdx],
          childrenLogs:
            childrenLogs.length === 1 ? childrenLogs[0].children : childrenLogs,
        };

        return cloned;
      });
    });
  };
  /** 상세 묶음(유저 연속 문서 편집 등) */
  const renderCompressedDetailLogs = (detailLogs: LogItem[]) => {
    let returnDetailLogs: LogItem[] = [];
    for (let i = 0; i < detailLogs.length; i++) {
      if (
        detailLogs[i].logType.includes("document") &&
        returnDetailLogs[returnDetailLogs.length - 1]?.userId ===
          detailLogs[i]?.userId
      ) {
        returnDetailLogs[returnDetailLogs.length - 1].children = [
          ...(returnDetailLogs[returnDetailLogs.length - 1]?.children ?? []),
          detailLogs[i],
        ];
      } else {
        returnDetailLogs.push({ ...detailLogs[i], children: [detailLogs[i]] });
      }
    }

    // flatten 제거 (depth 구조 보존)
    return returnDetailLogs;
  };

  /** 시간 범위 표기 (버그 수정) */
  const renderCompressedDateRange = (
    hasChildren: boolean,
    startLog: number,
    endLog?: number
  ) => {
    if (!hasChildren) return utilsCommon.renderDateFormat(startLog, "h:mm A");

    const startTime = utilsCommon.renderDateFormat(startLog, "h:mm A");
    const endTime = utilsCommon.renderDateFormat(endLog!, "h:mm A");

    if (startTime === endTime) return startTime;

    const startIsPM = dayjs(startLog).hour() >= 12;
    const endIsPM = dayjs(endLog!).hour() >= 12; // ← endLog 기준으로 수정
    if (startIsPM === endIsPM) {
      return utilsCommon.renderDateFormat(startLog, "h:mm") + "-" + endTime;
    }
    return startTime + "-" + endTime;
  };

  /** 아이콘 카운트 합산(run 단위) */
  const sumActionTypeCounts = (logArray: SummaryLogItem[]) => {
    const acc: Record<string, number> = {};
    logArray.forEach((l) => {
      Object.entries(l.actionTypeCounts || {}).forEach(([k, v]) => {
        if (v == null) return;
        acc[k] = (acc[k] ?? 0) + v;
      });
    });
    return acc;
  };

  const getActionTypeCounts = (
    data: LogItem[] | Record<string, number | undefined>
  ) => {
    if (Array.isArray(data)) {
      const uniqueKeys = data.filter(
        (v, i, a) =>
          i ===
          a.findIndex(
            (x) =>
              `${x.logType}_${x.actionType}` === `${v.logType}_${v.actionType}`
          )
      );
      return uniqueKeys.map((item) => {
        const key = `${item.logType}_${item.actionType}`;
        const count =
          data.filter((x) => `${x.logType}_${x.actionType}` === key).length ??
          0;
        return { key, count };
      });
    } else {
      return Object.entries(data)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => ({ key, count: value ?? 0 }));
    }
  };

  const renderLogIcon = (
    data: LogItem[] | Record<string, number | undefined>,
    isInBody?: boolean
  ) => {
    const dataSource = getActionTypeCounts(data);
    const merged = historyIconType
      .map((grp) => {
        const found = dataSource.filter((ds) => grp.includes(ds.key));
        return found.length
          ? { ...found[0], count: found.reduce((s, it) => s + it.count, 0) }
          : undefined;
      })
      .filter(Boolean) as { key: string; count: number }[];

    return (
      <Tooltip
        message={merged
          .map((it) => `${historyIconByName[it.key].name}: ${it.count}`)
          .join(", ")}
      >
        {merged
          .filter((_, index) => {
            const wide =
              window.innerWidth >
              (window.location.href.includes("/home") ? 2000 : 1540);
            return (index > 3 && wide) || index < 3 || !isInBody || wide;
          })
          .map((it) => (
            <Box alignItems="center" key={`icon-${it.key}`}>
              <Icons
                label={historyIconByName[it.key].icon}
                variant="line"
                size="16"
                isOn
              />
              <Typography type="subtitle2" classes="mg ml-6">
                {it.count}
              </Typography>
            </Box>
          ))}
        {isInBody &&
          merged.length > 3 &&
          (window.innerWidth < 1540 ||
            window.location.href.includes("/home")) && (
            <Typography style={{ marginInlineStart: 0 }} type="subtitle2">
              {"..."}
            </Typography>
          )}
      </Tooltip>
    );
  };

  /** 단일 줄 렌더 (run/summary/logitem 통합) */
  const renderLogEachItem = (
    received: SummaryLogItem | LogItem | SummaryLogItem[],
    id: string,
    depth: number,
    isLast?: boolean,
    isLastRoot?: boolean
  ) => {
    const log = Array.isArray(received) ? received[0] : received;
    const hasNoChildren =
      depth === 4 ||
      isLastRoot ||
      (Array.isArray(received) &&
        received.length <= 1 &&
        (received[0]?.logIds?.length ?? 0) <= 1) ||
      (!Array.isArray(received) &&
        "children" in received &&
        (!received.children || received.children?.length <= 1)) ||
      (!Array.isArray(received) &&
        "logIds" in received &&
        received.logIds?.length <= 1) ||
      (!Array.isArray(received) &&
        !("children" in received) &&
        !("logIds" in received));

    const chat =
      "logDetailInfo" in log && log.logDetailInfo?.chatLogData
        ? (JSON.parse(log.logDetailInfo?.chatLogData)[0] as ChatLogItem)
        : "chatLogData" in log && (log as any)?.chatLogData
        ? (JSON.parse((log as any)?.chatLogData)[0] as ChatLogItem)
        : undefined;

    const logId =
      "logIds" in log ? log.logIds[0] : "logId" in log ? log.logId : id;
    const newDepth =
      "involvedUsers" in log && hasNoChildren && depth > 1 ? 2 : depth;
    return (
      <Box
        height={!Array.isArray(received) && chat?.parentMessage ? 72 : 56}
        classes={classNames(`compressed-item--depth${newDepth}`, {
          selected:
            (docAsideIndex === logId && hasNoChildren) ||
            settingButtonInfo?.logId === ("logIds" in log ? log.logIds[0] : id),
          isOpen: checkboxGroup.isSelected(id) && newDepth <= 2,
          isLast: isLast,
        })}
        onClick={() => {
          if (hasNoChildren && changeDocAsideIndex) {
            changeDocAsideIndex(logId);
          }
        }}
        onContextMenu={(e) => {
          e.stopPropagation();
          e.preventDefault();
          hasNoChildren &&
            // "logIds" in log &&
            log?.logType?.includes("document") &&
            setSettingInfo &&
            setSettingInfo({
              x: dir === "ltr" ? e.pageX - 240 : e.pageX,
              y: e.pageY - 61,
              logId:
                settingButtonInfo?.logId === logId
                  ? settingButtonInfo.logId
                  : "logIds" in log
                  ? log.logIds[0]
                  : "logId" in log
                  ? log.logId
                  : id,
              log:
                settingButtonInfo?.logId === logId
                  ? settingButtonInfo.log
                  : undefined,
            });
        }}
      >
        <Box minWidth={newDepth === 1 ? 0 : 16} />
        {!hasNoChildren ? (
          <Button
            btnStyle="table-icon"
            size="sm"
            width={32}
            onClick={(e) => {
              e.stopPropagation();
              handleGetLogBelow(id);
            }}
            leadingIcon={
              <Icons
                variant="line"
                label="chevron"
                direction={checkboxGroup.isSelected(id) ? "down" : "right"}
              />
            }
          />
        ) : (
          // (newDepth <= 2 || pageType !== "workgroupLog") && (
          <Box width={32} height={32} />
          // )
        )}
        {"involvedUsers" in log && (!hasNoChildren || newDepth === 1) ? (
          <Box
            width={pageType === "workgroupLog" ? 160 : 120}
            alignItems="center"
            classes="mg ml-8 mr-20"
          >
            <Tooltip
              style={{ display: "flex" }}
              message={log.involvedUsers
                .map((item) =>
                  utilsCommon.getUserNameIdTag(item.userId, item.userName)
                )
                .join(", ")}
              maxWidth={pageType === "workgroupLog" ? 160 : 120}
            >
              <Box alignItems="center" width={"fit-content"}>
                <Typography
                  type={"subtitle2"}
                  classes="compressed-target"
                  maxWidth={pageType === "workgroupLog" ? 160 : 120}
                >
                  {utilsCommon.getUserNameIdTag(
                    log.involvedUsers[0].userId,
                    log.involvedUsers[0].userName,
                    pageType !== "workgroupLog"
                  ) +
                    (log.involvedUsers.length > 1
                      ? ` ${t("common.andNPeople", {
                          num: log.involvedUsers.length - 1,
                        })}`
                      : "")}
                </Typography>
              </Box>
            </Tooltip>
          </Box>
        ) : (
          <>
            {!hasNoChildren ? (
              <Box width={8} />
            ) : (
              <Typography
                type={
                  "logId" in log &&
                  checkboxGroup.isSelected(log.logId) &&
                  newDepth <= 2
                    ? "subtitle2"
                    : "body2_rg"
                }
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
                // width={150}
                width={pageType === "workgroupLog" ? 160 : 120}
                classes="mg ml-8 mr-20"
                textAlign="start"
              >
                {Array.isArray(received)
                  ? renderCompressedDateRange(
                      true,
                      received[0].firstDate,
                      received[0].lastDate
                    )
                  : "involvedUsers" in log
                  ? renderCompressedDateRange(
                      true,
                      (received as any).firstDate,
                      (received as any).lastDate
                    )
                  : renderCompressedDateRange(
                      !!(log as any)?.children &&
                        (log as any).children?.length > 1,
                      !(log as any).children ||
                        (log as any).children?.length <= 1
                        ? (log as any)?.logDate
                        : (log as any).children[
                            (log as any).children.length - 1
                          ]?.logDate,
                      (log as any).children
                        ? (log as any).children[0]?.logDate
                        : undefined
                    )}
              </Typography>
            )}

            <Box
              width={
                pageType !== "workgroupLog" ? 120 : hasNoChildren ? 140 : 160
              }
              justifyContent="flex-start"
              classes="mg mr-20"
            >
              <Tooltip
                message={utilsCommon.getUserNameIdTag(
                  (log as any).userId,
                  (log as any).userName
                )}
              >
                <Typography type={"subtitle2"} ellipsis>
                  {"involvedUsers" in log
                    ? utilsCommon.getUserNameIdTag(
                        log.involvedUsers[0].userId,
                        log.involvedUsers[0].userName,
                        pageType !== "workgroupLog"
                      ) +
                      (log.involvedUsers.length > 1
                        ? t("common.andNPeople", {
                            num: log.involvedUsers.length - 1,
                          })
                        : "")
                    : utilsCommon.getUserNameIdTag(
                        (log as any).userId,
                        (log as any).userName ?? (log as any)?.involvedUsers,
                        pageType !== "workgroupLog"
                      )}
                </Typography>
              </Tooltip>
            </Box>
          </>
        )}
        {renderLogDetailStr(received, id, !hasNoChildren)}

        {hasNoChildren && (log as any).logType !== "chat" ? (
          renderActionDetail(log as any, id)
        ) : (log as any).logType !== "chat" &&
          "children" in (log as any) &&
          (log as any).children ? (
          <Box classes="compressed-simpleActions" style={{ flexShrink: 0 }}>
            {renderLogIcon((log as any).children, true)}
          </Box>
        ) : (
          (log as any).logType !== "chat" &&
          "involvedUsers" in log &&
          // log.involvedUsers.length === 1 &&
          depth !== 1 && (
            <Box classes="compressed-simpleActions" style={{ flexShrink: 0 }}>
              {renderLogIcon(sumActionTypeCounts([log]), true)}
            </Box>
          )
        )}
        <Box classes="actionButtons">
          {log.logType.includes("document") && hasNoChildren && (
            <Button
              btnStyle={"table-icon"}
              size="sm"
              classes="mg ml-4"
              onClick={(e) => {
                e.stopPropagation();
                setSettingInfo &&
                  setSettingInfo({
                    x: dir === "ltr" ? e.pageX - 240 : e.pageX,
                    y: e.pageY - 61,
                    logId:
                      settingButtonInfo?.logId === logId
                        ? settingButtonInfo.logId
                        : "logIds" in log
                        ? log.logIds[0]
                        : "logId" in log
                        ? log.logId
                        : id,
                    log:
                      settingButtonInfo?.logId === logId
                        ? settingButtonInfo.log
                        : undefined,
                  });
              }}
              leadingIcon={<Icons variant="line" label="moreVertical" />}
            />
          )}
        </Box>
      </Box>
    );
  };

  /** run 카드 */
  const renderLogItem = (run: RunGroup, index: string) => {
    const head = run[0];
    const last = run[run.length - 1];
    return (
      <Box
        classes="compressed-item"
        direction="column"
        width={"calc(100% - 28px)"}
        key={index}
      >
        <Box
          classes="compressed-item--icon"
          alignItems="center"
          justifyContent="center"
        />
        <Box alignItems="center">
          <Typography type="body2_rg">
            {renderCompressedDateRange(
              head.logIds.length > 1 || run.length > 1,
              head.logIds.length <= 1 && run.length === 1
                ? head.firstDate
                : last.lastDate,
              head.firstDate
            )}
          </Typography>
          <Box classes="compressed-simpleActions">
            {renderLogIcon(sumActionTypeCounts(run))}
          </Box>
        </Box>

        {/* run 대표 줄 */}
        {renderLogEachItem(run, index, 1)}
        {/* run 내부 요약들(펼치기) */}
        {checkboxGroup.isSelected(index) &&
          run.map((item, ridx) => {
            const itemId = `${index}--${ridx}`;
            return (
              <Box direction="column" key={itemId}>
                {run.length === 1 ? (
                  <>
                    {item.childrenLogs?.map((item4, index4) => {
                      const leafId = `${itemId}--${index4}`;
                      return (
                        <Box direction="column" key={leafId}>
                          {renderLogEachItem(
                            item4,
                            leafId,
                            2,
                            ridx === run.length - 1 &&
                              index4 === (item.childrenLogs?.length ?? 0) - 1 &&
                              !checkboxGroup.isSelected(leafId)
                          )}
                          {checkboxGroup.isSelected(leafId) &&
                            item4.children?.map((secondLog, index3) =>
                              renderLogEachItem(
                                secondLog,
                                `${leafId}--${index3}`,
                                3,
                                ridx === run.length - 1 &&
                                  index4 ===
                                    (item.childrenLogs?.length ?? 0) - 1 &&
                                  index3 === (item4.children?.length ?? 0) - 1,
                                true
                              )
                            )}
                        </Box>
                      );
                    })}
                  </>
                ) : (
                  <>
                    {renderLogEachItem(
                      item,
                      itemId,
                      2,
                      ridx === run.length - 1 &&
                        !checkboxGroup.isSelected(itemId)
                    )}
                    {checkboxGroup.isSelected(itemId) &&
                      item.childrenLogs?.map((item4, index4) => {
                        const leafId = `${itemId}--${index4}`;
                        return (
                          <Box direction="column" key={leafId}>
                            {renderLogEachItem(
                              item4,
                              leafId,
                              3,
                              ridx === run.length - 1 &&
                                index4 ===
                                  (item.childrenLogs?.length ?? 0) - 1 &&
                                !checkboxGroup.isSelected(leafId)
                            )}
                            {checkboxGroup.isSelected(leafId) &&
                              item4.children?.map((secondLog, index3) =>
                                renderLogEachItem(
                                  secondLog,
                                  `${leafId}--${index3}`,
                                  4,
                                  ridx === run.length - 1 &&
                                    index4 ===
                                      (item.childrenLogs?.length ?? 0) - 1 &&
                                    index3 ===
                                      (item4.children?.length ?? 0) - 1,
                                  true
                                )
                              )}
                          </Box>
                        );
                      })}
                  </>
                )}
              </Box>
            );
          })}
      </Box>
    );
  };

  /** 렌더 */
  return (
    <Box
      direction="column"
      width={"100%"}
      id={"compressedLog"}
      classes="compressed"
      style={{ overflow: "auto" }}
    >
      {logs && logs.filter((day) => day.length > 0).length > 0 ? (
        logs
          .filter((day) => day.length > 0)
          .map((dayLogs, dIdx) => (
            <Box width={"100%"} key={`day-${dIdx}`}>
              <Box direction="column" minWidth={80}>
                <Typography type="subtitle2">
                  {utilsCommon.renderDateFormat(
                    dayLogs[0][0].firstDate,
                    "MMM D" + (i18n.language.includes("ko") ? "일" : ""),
                    i18n.language
                  )}
                </Typography>
                <Typography
                  type="body3_rg"
                  classes="mg mt-2"
                  exactColor={getColor("COLOR_TEXT_SECONDARY")}
                >
                  {utilsCommon.renderDateFormat(
                    dayLogs[0][0].firstDate,
                    "dddd",
                    i18n.language
                  )}
                </Typography>
              </Box>

              <Box direction="column" width={"calc(100% - 80px)"}>
                {dayLogs.map((run, rIdx) =>
                  renderLogItem(
                    run,
                    `${utilsCommon.renderDateFormat(
                      dayLogs[0][0].firstDate,
                      "YYYY-MM-DD"
                    )}--${rIdx}`
                  )
                )}
              </Box>
            </Box>
          ))
      ) : !dateFilter ||
        (dayjs().year() === parseInt(dateFilter.year) &&
          dayjs().month() === parseInt(dateFilter.month)) ? (
        <EmptyInitFilterPage
          pageType={
            pageType === "workgroupHome"
              ? "workgroup_home_log"
              : pageType === "home"
              ? "home_log"
              : "compressed_log"
          }
        />
      ) : (
        <EmptyFilterPage
          handleInitFilter={handleInitFilter}
          pageType={"compressed_log"}
        />
      )}
    </Box>
  );
};

export default CompressWorkgroupLog;
